import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const STEPS = [
  {
    id: 1,
    title: '¡Bienvenido a AMOXTLI! 🎉',
    content: `Esta guía te ayudará a empezar. AMOXTLI te permite gestionar operación fiscal 
    y obligaciones tributarias de forma segura y automatizada.`,
    highlight: 'dashboard',
    action: 'Empecemos'
  },
  {
    id: 2,
    title: 'Paso 1: Registra una Empresa',
    content: `Primero necesitas registrar contribuyentes (RFC, razón social). Puedes gestionar
    múltiples empresas - Personas Físicas o Morales.`,
    highlight: 'empresas',
    action: 'Ir a Empresas',
    route: '/empresas'
  },
  {
    id: 3,
    title: 'Paso 2: Carga Credenciales SAT (Seguras)',
    content: `Después de crear una empresa, carga su FIEL o CSD. Los certificados se cifran 
    automáticamente antes de guardarse. ¡Nunca en texto plano!`,
    highlight: 'empresas',
    action: 'Siguiente'
  },
  {
    id: 4,
    title: 'Paso 3: Gestiona Facturas (CFDI)',
    content: `Importa tus comprobantes fiscales en XML. El sistema valida estructura y calcula
    totales automáticamente.`,
    highlight: 'facturas',
    action: 'Ver Facturas',
    route: '/facturas'
  },
  {
    id: 5,
    title: 'Paso 4: Calcula Declaraciones',
    content: `El motor fiscal calcula ISR e IVA según tu régimen (601, 626 RESICO, etc.) usando
    las tablas oficiales actualizadas por ejercicio.`,
    highlight: 'declaraciones',
    action: 'Ver Declaraciones',
    route: '/declaraciones'
  },
  {
    id: 6,
    title: 'Paso 5: Genera Líneas de Captura',
    content: `Crea líneas de captura para pagar tus impuestos en el banco. Luego confirma
    el pago con el folio SAT.`,
    highlight: 'pagos',
    action: 'Ver Pagos',
    route: '/pagos'
  },
  {
    id: 7,
    title: 'Seguridad: Configura 2FA (Recomendado) 🔒',
    content: `Protege tu cuenta con autenticación de doble factor. Solo toma 2 minutos con
    Google Authenticator o Authy.`,
    highlight: 'perfil',
    action: 'Configurar 2FA',
    route: '/perfil'
  },
  {
    id: 8,
    title: '¡Todo Listo! 🚀',
    content: `Ya conoces las funciones principales. Explora Reportes para analytics, revisa el
    manual completo en docs/MANUAL_USUARIO.md, y no olvides cambiar tu contraseña.`,
    highlight: 'dashboard',
    action: 'Finalizar',
    final: true
  }
]

export function OnboardingGuide({ onComplete }) {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [show, setShow] = useState(false)

  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('onboarding_completed')
    if (!hasSeenGuide) {
      // Mostrar guía después de medio segundo
      setTimeout(() => setShow(true), 500)
    }
  }, [])

  const handleNext = () => {
    const step = STEPS[currentStep]
    
    if (step.final) {
      localStorage.setItem('onboarding_completed', 'true')
      setShow(false)
      if (onComplete) onComplete()
      return
    }

    if (step.route) {
      // Navegar sin recargar para preservar estado global.
      navigate(step.route)
      setCurrentStep((prev) => prev + 1)
    } else {
      setCurrentStep((prev) => prev + 1)
    }
  }

  const handleSkip = () => {
    localStorage.setItem('onboarding_completed', 'true')
    setShow(false)
    if (onComplete) onComplete()
  }

  const handleRestart = () => {
    setCurrentStep(0)
    setShow(true)
  }

  // Botón flotante para reabrir guía (IZQUIERDA)
  if (!show) {
    return (
      <button
        onClick={handleRestart}
        className="fixed bottom-4 left-4 z-40 rounded-full bg-indigo-600 p-3 text-white shadow-lg hover:bg-indigo-700"
        title="Abrir guía interactiva"
      >
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
    )
  }

  const step = STEPS[currentStep]
  const progress = ((currentStep + 1) / STEPS.length) * 100

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="relative w-full max-w-2xl rounded-lg bg-white p-8 shadow-2xl">
        {/* Botón de cerrar */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none"
          title="Cerrar guía"
        >
          ×
        </button>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-indigo-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-2 text-right text-xs text-gray-500">
            Paso {currentStep + 1} de {STEPS.length}
          </p>
        </div>

        {/* Content */}
        <div className="mb-8">
          <h2 className="mb-4 text-3xl font-bold text-gray-900">{step.title}</h2>
          <p className="text-lg leading-relaxed text-gray-700">{step.content}</p>
        </div>

        {/* Ilustración según paso */}
        <div className="mb-6 flex justify-center">
          {step.id === 1 && (
            <div className="text-6xl">📊</div>
          )}
          {step.id === 2 && (
            <div className="text-6xl">🏢</div>
          )}
          {step.id === 3 && (
            <div className="text-6xl">🔐</div>
          )}
          {step.id === 4 && (
            <div className="text-6xl">📄</div>
          )}
          {step.id === 5 && (
            <div className="text-6xl">🧮</div>
          )}
          {step.id === 6 && (
            <div className="text-6xl">💳</div>
          )}
          {step.id === 7 && (
            <div className="text-6xl">🛡️</div>
          )}
          {step.id === 8 && (
            <div className="text-6xl">✅</div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-gray-500 hover:text-gray-700"
          >
            Saltar guía
          </button>

          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep((prev) => prev - 1)}
                className="rounded border border-gray-300 px-6 py-2 text-gray-700 hover:bg-gray-50"
              >
                Anterior
              </button>
            )}
            <button
              onClick={handleNext}
              className="rounded bg-indigo-600 px-6 py-2 text-white hover:bg-indigo-700"
            >
              {step.action || 'Siguiente'}
            </button>
          </div>
        </div>

        {/* Tips adicionales */}
        {step.id === 7 && (
          <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Si gestionas credenciales SAT de clientes,
              el 2FA es esencial para cumplir con mejores prácticas de seguridad.
            </p>
          </div>
        )}

        {step.id === 8 && (
          <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4">
            <p className="text-sm text-green-800">
              📖 <strong>Documentación completa:</strong> Revisa{' '}
              <code className="rounded bg-green-100 px-1">docs/MANUAL_USUARIO.md</code>{' '}
              para guías detalladas de cada módulo.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default OnboardingGuide
